// Hub ▸ Marketing — shared pieces for the S2 screens (CEO 2026-10-21):
// number formats, the "how it's worked out" tip, panels, loading skeletons
// with Retry, empty states, status pills, ONE sortable table (sticky first
// column, expandable child rows, ⓘ after each label), CSV download (formula
// safe), the period picker and the data-freshness stamp. Brand tokens only;
// large type, plain words. Nothing here invents a number: no data is "—",
// a real zero is shown dimmed.
import { Fragment, useCallback, useEffect, useId, useRef, useState, type ReactNode } from "react";
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight, ChevronsUpDown, Info, RefreshCw } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";
import { cn } from "@/lib/utils";
import { toCsv } from "./csv";
import { PRESET_VALUES, type PeriodState, type Preset } from "./period";

export { toCsv };
export type { PeriodState, Preset };

/** Flow (staff app). */
export const FLOW = "https://flow.withother.com";
export const flowLink = {
  client: (id: string) => `${FLOW}/clients/${id}`,
  order: (id: string) => `${FLOW}/orders/${id}`,
  project: (id: string) => `${FLOW}/projects/${id}`,
};

// ─── formats ────────────────────────────────────────────────────────────────

export type Unit = "currency_cents" | "ratio" | "count" | "months" | "percent" | "currency_cents_per_day" | "days";
export function money(cents: number | null | undefined, opts: { compact?: boolean; cents?: boolean } = {}): string {
  if (cents == null) return "—";
  const v = cents / 100;
  const abs = Math.abs(v);
  let s: string;
  if (opts.compact && abs >= 10_000) s = `$${abs >= 1_000_000 ? `${(abs / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M` : `${(abs / 1000).toFixed(abs >= 100_000 ? 0 : 1)}k`}`;
  else s = abs.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: opts.cents ? 2 : 0, minimumFractionDigits: opts.cents ? 2 : 0 });
  return v < 0 ? `−${s}` : s;
}
export const count = (n: number | null | undefined) => (n == null ? "—" : n.toLocaleString("en-US"));
export function fmtValue(unit: Unit, v: number | null | undefined, compact = false): string {
  if (v == null) return "—";
  switch (unit) {
    case "currency_cents": return money(v, { compact });
    case "currency_cents_per_day": return `${money(v, { compact })}/day`;
    case "days": { const d = Math.round(v); return `${d.toLocaleString("en-US")} day${d === 1 ? "" : "s"}`; }
    case "ratio": return `${v.toFixed(2)}×`;
    case "percent": return `${(v * 100).toFixed(1)}%`;
    case "months": return `${v.toFixed(1)} mo`;
    default: return count(v);
  }
}
/** A number cell: "—" for no data, a real zero dimmed. */
export function Num({ v, children }: { v: number | null | undefined; children: ReactNode }) {
  if (v == null) return <span className="text-ink-muted">—</span>;
  return <span className={cn(v === 0 && "text-ink-muted/70")}>{children}</span>;
}
export const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const shortDay = (iso: string | null | undefined) => {
  if (!iso) return "—";
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00Z` : iso);
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
};
export const monthLabel = (m: string, withYear = true) => `${MONTHS[Number(m.slice(5, 7)) - 1]}${withYear ? ` ${m.slice(0, 4)}` : ""}`;

// ─── data hook ──────────────────────────────────────────────────────────────

export function useApi<T>(path: string | null): { data: T | null; error: string | null; loading: boolean; reload: () => void } {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!path) return;
    let live = true;
    setLoading(true);
    setError(null);
    api<T>(path)
      .then((d) => { if (live) setData(d); })
      .catch((e) => { if (live) setError(apiErrorMessage(e, "Couldn't load this.")); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [path, n]);
  return { data, error, loading, reload: useCallback(() => setN((x) => x + 1), []) };
}

// ─── layout pieces ──────────────────────────────────────────────────────────

export function Panel({ title, info, actions, children, className, id, caption }: { title?: ReactNode; info?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string; id?: string; caption?: ReactNode }) {
  return (
    <section id={id} className={cn("min-w-0 rounded-lg border border-rule bg-white", className)}>
      {(title || actions) && (
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-rule px-4 py-4 sm:px-6">
          <div>
            <h2 className="flex items-center gap-1 text-[20px] font-semibold leading-tight text-ink">{title}{info}</h2>
            {caption && <p className="mt-1 text-label text-ink-muted">{caption}</p>}
          </div>
          {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
        </header>
      )}
      <div className="px-4 py-5 sm:px-6">{children}</div>
    </section>
  );
}

export function EmptyState({ title, children, action }: { title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="rounded-lg border border-dashed border-rule bg-paper px-5 py-7">
      <p className="text-label font-semibold text-ink">{title}</p>
      {children && <div className="mt-1.5 max-w-3xl text-body text-ink-muted">{children}</div>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function ErrorLine({ children, onRetry }: { children: ReactNode; onRetry?: () => void }) {
  return (
    <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-risk/30 bg-risk-bg px-4 py-3 text-label text-risk">
      <span>{children}</span>
      {onRetry && <button type="button" onClick={onRetry} className="inline-flex h-9 items-center gap-1.5 rounded-md border border-risk/40 bg-white px-3 font-semibold text-risk hover:bg-risk-bg"><RefreshCw className="h-4 w-4" aria-hidden />Retry</button>}
    </div>
  );
}

/** Grey placeholder blocks in the page's shape while the numbers load. */
export function Skeleton({ rows = 3, tiles = 0, label = "Loading" }: { rows?: number; tiles?: number; label?: string }) {
  return (
    <div role="status" aria-label={label} className="space-y-4">
      {tiles > 0 && <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">{Array.from({ length: tiles }, (_, i) => <div key={i} className="h-36 animate-pulse rounded-lg bg-paper" />)}</div>}
      {Array.from({ length: rows }, (_, i) => <div key={i} className="h-48 animate-pulse rounded-lg bg-paper" />)}
      <span className="sr-only">{label}…</span>
    </div>
  );
}

export type Tone = "ok" | "warn" | "risk" | "neutral";
export function Pill({ tone, children }: { tone: Tone; children: ReactNode }) {
  const cls = tone === "ok" ? "bg-ok-bg text-ok" : tone === "warn" ? "bg-warn-bg text-warn" : tone === "risk" ? "bg-risk-bg text-risk" : "bg-paper text-ink-muted";
  return <span className={cn("inline-flex items-center gap-1.5 whitespace-nowrap rounded px-2 py-0.5 text-meta font-semibold", cls)}>
    <span aria-hidden className={cn("h-1.5 w-1.5 rounded-full", tone === "ok" ? "bg-ok" : tone === "warn" ? "bg-warn" : tone === "risk" ? "bg-risk" : "bg-ink-muted")} />{children}
  </span>;
}

/** "How it's worked out": the formula on hover, focus or tap. */
export function InfoTip({ label, formula, notes, align = "left" }: { label: string; formula: string; notes?: string[]; align?: "left" | "right" }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent | KeyboardEvent) => {
      if (e instanceof KeyboardEvent ? e.key === "Escape" : !ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", close);
    return () => { document.removeEventListener("mousedown", close); document.removeEventListener("keydown", close); };
  }, [open]);
  return (
    <span ref={ref} className="relative inline-flex normal-case tracking-normal" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button type="button" aria-label={`How ${label} is worked out`} aria-expanded={open} aria-describedby={open ? id : undefined}
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }} onFocus={() => setOpen(true)} onBlur={() => setOpen(false)}
        className="inline-flex h-7 w-7 items-center justify-center rounded text-ink-muted hover:bg-paper hover:text-ink">
        <Info className="h-4 w-4" aria-hidden />
      </button>
      {open && (
        <span id={id} role="tooltip" className={cn("absolute top-8 z-30 w-[min(340px,80vw)] rounded-md border border-rule bg-white p-4 text-left text-label font-normal leading-snug text-ink shadow-md", align === "right" ? "right-0" : "left-0")}>
          <span className="block text-meta font-semibold uppercase tracking-[0.08em] text-ink-muted">How it's worked out</span>
          <span className="mt-1 block">{formula}</span>
          {notes?.map((n) => <span key={n} className="mt-2 block text-meta text-warn">{n}</span>)}
        </span>
      )}
    </span>
  );
}

export function Segmented<T extends string>({ value, options, onChange, label }: { value: T; options: Array<{ value: T; label: string }>; onChange: (v: T) => void; label: string }) {
  return (
    <div role="radiogroup" aria-label={label} className="inline-flex max-w-full flex-wrap rounded-lg border border-rule bg-paper p-1">
      {options.map((o) => (
        <button key={o.value} type="button" role="radio" aria-checked={value === o.value} onClick={() => onChange(o.value)}
          className={cn("min-h-[36px] whitespace-nowrap rounded-md px-3 text-label transition-colors", value === o.value ? "bg-ink font-semibold text-white" : "text-ink hover:bg-white")}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ─── period picker ──────────────────────────────────────────────────────────

export interface ResolvedPeriod { from: string; to: string; label: string }
const PRESET_LABEL: Record<Preset, string> = { this_month: "This month", last_month: "Last month", quarter: "This quarter", ytd: "Year to date", custom: "Custom" };
export const PRESETS: Array<{ value: Preset; label: string }> = PRESET_VALUES.map((v) => ({ value: v, label: PRESET_LABEL[v] }));
export function periodQuery(p: PeriodState): string {
  return p.preset === "custom" ? `preset=custom&from=${p.from}&to=${p.to}` : `preset=${p.preset}`;
}
export function PeriodPicker({ value, onChange, resolved, compare }: { value: PeriodState; onChange: (p: PeriodState) => void; resolved?: ResolvedPeriod | null; compare?: ResolvedPeriod | null }) {
  const [draft, setDraft] = useState({ from: value.from, to: value.to });
  const valid = /^(19|20)\d{2}-\d{2}-\d{2}$/.test(draft.from) && /^(19|20)\d{2}-\d{2}-\d{2}$/.test(draft.to) && draft.from <= draft.to;
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <Segmented label="Dates" value={value.preset} options={PRESETS} onChange={(preset) => onChange({ ...value, preset })} />
        {value.preset === "custom" && (
          <form className="flex flex-wrap items-center gap-2" onSubmit={(e) => { e.preventDefault(); if (valid) onChange({ preset: "custom", ...draft }); }}>
            <label className="sr-only" htmlFor="mk-from">From</label>
            <input id="mk-from" type="date" min="2000-01-01" max="2099-12-31" value={draft.from} onChange={(e) => setDraft({ ...draft, from: e.target.value })} className="h-10 rounded-md border border-rule px-3 text-label" />
            <span className="text-ink-muted">to</span>
            <label className="sr-only" htmlFor="mk-to">To</label>
            <input id="mk-to" type="date" min="2000-01-01" max="2099-12-31" value={draft.to} onChange={(e) => setDraft({ ...draft, to: e.target.value })} className="h-10 rounded-md border border-rule px-3 text-label" />
            <button type="submit" disabled={!valid} className="h-10 rounded-md bg-ink px-4 text-label font-semibold text-white disabled:opacity-40">Show</button>
          </form>
        )}
      </div>
      {resolved && (
        <p className="text-label text-ink-muted">
          <span className="whitespace-nowrap font-semibold text-ink">{resolved.label}</span>
          {compare && <> <span className="whitespace-nowrap">· vs {compare.label}</span></>}
        </p>
      )}
    </div>
  );
}

// ─── freshness ──────────────────────────────────────────────────────────────

export interface Freshness { lastOkAt: string | null; lastFailedAt?: string | null; lastRunAt: string | null; lastStatus: string | null; lastError: string | null }
export function FreshnessStamp({ freshness, isAdmin, onRefreshed, throughDay }: { freshness: Freshness | null | undefined; isAdmin: boolean; onRefreshed?: () => void; throughDay?: string | null }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const when = (iso: string) => new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
  const at = freshness?.lastOkAt ? when(freshness.lastOkAt) : null;
  // A failure newer than the last good build: say so plainly (the numbers shown are older).
  const failedAt = freshness?.lastFailedAt && (!freshness.lastOkAt || freshness.lastFailedAt > freshness.lastOkAt) ? when(freshness.lastFailedAt) : null;
  async function refresh() {
    setBusy(true); setMsg(null);
    try {
      await api("/api/marketing/analytics/refresh", { method: "POST" });
      setMsg("Rebuilding — this takes about a minute.");
      const started = Date.now();
      const poll = async () => {
        const s = await api<{ freshness: Freshness }>("/api/marketing/analytics/status").catch(() => null);
        if (s?.freshness.lastOkAt && (!freshness?.lastOkAt || s.freshness.lastOkAt > freshness.lastOkAt)) { setBusy(false); setMsg(null); onRefreshed?.(); return; }
        if (s?.freshness.lastStatus === "failed" && s.freshness.lastRunAt && Date.parse(s.freshness.lastRunAt) >= started - 5000) { setBusy(false); setMsg("The rebuild failed — the numbers shown are from the last good build."); return; }
        if (Date.now() - started > 5 * 60_000) { setBusy(false); setMsg("Still rebuilding — reload the page in a minute."); return; }
        setTimeout(poll, 4000);
      };
      setTimeout(poll, 4000);
    } catch (e) {
      setBusy(false); setMsg(apiErrorMessage(e, "Couldn't start the rebuild."));
    }
  }
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-meta text-ink-muted">
      <span>{at ? <>Numbers as of <span className="whitespace-nowrap font-semibold text-ink">{at}</span>{throughDay ? <> · spend counted through <span className="whitespace-nowrap">{shortDay(throughDay)}</span></> : null} · rebuilt nightly</> : "Numbers not built yet — the first build runs tonight."}</span>
      {failedAt && <Pill tone="risk">Last refresh failed at {failedAt}{at ? " — showing the last good build" : ""}</Pill>}
      {isAdmin && (
        <button type="button" onClick={refresh} disabled={busy} className="inline-flex items-center gap-1.5 rounded px-2 py-1 font-semibold text-ink underline underline-offset-4 hover:bg-paper disabled:opacity-50">
          <RefreshCw className={cn("h-3.5 w-3.5", busy && "animate-spin")} aria-hidden /> Refresh now
        </button>
      )}
      {msg && <span>{msg}</span>}
    </div>
  );
}

// ─── the one sortable table ─────────────────────────────────────────────────

export interface Column<R> {
  key: string;
  label: string;
  /** Sort value. */
  value: (r: R) => number | string | null;
  render?: (r: R) => ReactNode;
  numeric?: boolean;
  info?: string;
  className?: string;
}
export function SortTable<R>({ rows, columns, initialSort, rowKey, caption, footer, rowClass, childRows, stickyFirst, minWidth = 640, colWidths }: {
  rows: R[]; columns: Column<R>[]; initialSort?: { key: string; dir: "asc" | "desc" }; rowKey: (r: R) => string; caption: string; footer?: ReactNode;
  rowClass?: (r: R) => string | undefined;
  /** Expandable child rows (e.g. a channel's campaigns), sorted by the same column. */
  childRows?: (r: R) => R[];
  /** Keep the first column visible while scrolling sideways (~220px). */
  stickyFirst?: boolean;
  minWidth?: number;
  /** Fixed column widths (CSS), one per column: the table lays out fixed, so two tables with the same widths line up. */
  colWidths?: string[];
}) {
  const [sort, setSort] = useState(initialSort ?? { key: columns[0]!.key, dir: "asc" as const });
  const [open, setOpen] = useState<Set<string>>(new Set());
  const col = columns.find((c) => c.key === sort.key) ?? columns[0]!;
  const cmp = (a: R, b: R) => {
    const x = col.value(a); const y = col.value(b);
    if (x === y) return 0;
    if (x === null) return 1;
    if (y === null) return -1;
    const r = typeof x === "number" && typeof y === "number" ? x - y : String(x).localeCompare(String(y));
    return sort.dir === "asc" ? r : -r;
  };
  const sorted = [...rows].sort(cmp);
  const sticky = (i: number) => (stickyFirst && i === 0 ? "sticky left-0 z-[1] w-[150px] min-w-[150px] max-w-[150px] bg-inherit sm:w-[220px] sm:min-w-[220px] sm:max-w-[220px]" : "");
  const pad = "px-3 first:pl-4 last:pr-4 sm:first:pl-6 sm:last:pr-6";
  const cells = (r: R, child = false) => columns.map((c, i) => (
    <td key={c.key} className={cn(pad, "py-3 align-top", c.numeric && "text-right tabular-nums", c.className, sticky(i), child && i === 0 && "pl-10 sm:pl-12")}>
      {i === 0 && childRows && !child ? (
        <button type="button" aria-expanded={open.has(rowKey(r))} onClick={() => setOpen((s) => { const n = new Set(s); if (n.has(rowKey(r))) n.delete(rowKey(r)); else n.add(rowKey(r)); return n; })}
          className="inline-flex items-start gap-1 text-left font-semibold text-ink hover:underline">
          {open.has(rowKey(r)) ? <ChevronDown className="mt-1 h-4 w-4 shrink-0" aria-hidden /> : <ChevronRight className="mt-1 h-4 w-4 shrink-0" aria-hidden />}
          <span>{c.render ? c.render(r) : String(c.value(r) ?? "—")}</span>
        </button>
      ) : c.render ? c.render(r) : String(c.value(r) ?? "—")}
    </td>
  ));
  return (
    <div className="relative -mx-4 overflow-x-auto sm:-mx-6">
      <table className="w-full border-collapse text-label" style={{ minWidth, tableLayout: colWidths ? "fixed" : undefined }}>
        <caption className="sr-only">{caption}</caption>
        {colWidths && <colgroup>{colWidths.map((w, i) => <col key={i} style={{ width: w }} />)}</colgroup>}
        <thead>
          <tr className="border-b border-rule bg-white">
            {columns.map((c, i) => {
              const active = c.key === sort.key;
              return (
                <th key={c.key} scope="col" aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
                  className={cn(pad, "py-2.5 align-bottom text-meta font-semibold uppercase leading-tight tracking-[0.06em] text-ink-muted", c.numeric ? "min-w-[96px] text-right" : "text-left", sticky(i), i === 0 && stickyFirst && "bg-white")}>
                  <span className={cn("inline-flex items-end gap-0.5", c.numeric && "justify-end")}>
                    <button type="button" onClick={() => setSort({ key: c.key, dir: active && sort.dir === "desc" ? "asc" : active ? "desc" : c.numeric ? "desc" : "asc" })}
                      className={cn("inline-flex items-center gap-1 rounded px-1 py-0.5 text-left uppercase hover:bg-paper hover:text-ink", c.numeric && "text-right", active && "text-ink")}>
                      {c.label}
                      {active ? (sort.dir === "asc" ? <ArrowUp className="h-3 w-3" aria-hidden /> : <ArrowDown className="h-3 w-3" aria-hidden />) : <ChevronsUpDown className="h-3 w-3 opacity-40" aria-hidden />}
                    </button>
                    {c.info && <InfoTip label={c.label} formula={c.info} align={c.numeric ? "right" : "left"} />}
                  </span>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sorted.map((r) => (
            <Fragment key={rowKey(r)}>
              <tr className={cn("border-b border-rule/70 bg-white last:border-0", childRows && "bg-[#FAFAF8]", rowClass?.(r))}>{cells(r)}</tr>
              {childRows && open.has(rowKey(r)) && [...childRows(r)].sort(cmp).map((c) => (
                <tr key={`${rowKey(r)}>${rowKey(c)}`} className="border-b border-rule/50 bg-white">{cells(c, true)}</tr>
              ))}
            </Fragment>
          ))}
        </tbody>
        {footer}
      </table>
    </div>
  );
}

// ─── CSV ────────────────────────────────────────────────────────────────────

export function downloadText(filename: string, text: string, type = "text/csv") {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
